import { isEmpty } from '@intelligence/v3/utils/common';
import { GLOBAL_REGION } from '@intelligence/v3/constants/region';
import { getOptionText } from '@intelligence/utils/topchartsUtils';
import * as filterKeyMap from '@intelligence/components/Filter/FilterBase/_config';

/** 特殊市场快捷键（不是真实国家码） */
const SPECIAL_MARKETS = ['global', 'global_wo_china'];

/**
 * 根据国家/市场 codes 和 options 智能推导出完整的 region 筛选器内部值
 *
 * RegionSelector 内部需要 { type, regions, markets } 三字段，
 * 但上层只需维护 markets（国家码数组），type 和 regions 均可从 options 数据推导：
 *
 * - markets = ['global'] / ['global_wo_china'] → type: 'market', regions: []
 * - 某个 area 的所有子国家都在 markets 中 → 该 area 加入 regions，type: 'region'
 * - 有 area 的子国家部分选中 → type: 'market'
 *
 * @param markets  已选国家/市场码数组，如 ['global']、['US', 'CN', 'JP']
 * @param options  地区 options 数据（IRegion[]）
 */
export const deriveRegionValue = (
  markets: string[],
  options: IRegion[],
): { type: IRegionType; regions: string[]; markets: string[] } => {
  const safeMarkets = Array.isArray(markets) && markets.length > 0 ? markets : ['global'];

  // 特殊市场快捷键，无需推导
  if (safeMarkets.length === 1 && SPECIAL_MARKETS.includes(safeMarkets[0])) {
    return { type: 'market', regions: [], markets: safeMarkets };
  }

  if (!Array.isArray(options) || options.length === 0) {
    return { type: 'market', regions: [], markets: safeMarkets };
  }

  // 构建大小写不敏感的规范化 map（key: 小写, value: options 中的原始大小写）
  // 解决外部传入 'us' 而 options 使用 'US' 的大小写不一致问题
  const codeNormalizeMap = new Map<string, string>();
  options.forEach((area) => {
    (area?.country_set || []).forEach((sub: ICountry) => {
      codeNormalizeMap.set(sub.country_code.toLowerCase(), sub.country_code);
    });
  });

  // 将输入 markets 规范化为 options 中的大小写格式
  const normalizedMarkets = safeMarkets.map((code) => codeNormalizeMap.get(code.toLowerCase()) ?? code);

  const marketsSet = new Set(normalizedMarkets);
  const areaCodes: string[] = [];
  let scopeType: IRegionType = 'region';

  options.forEach((area) => {
    const children = area?.country_set || [];
    if (children.length === 0) {
      return;
    }

    const allIncluded = children.every((sub: ICountry) => marketsSet.has(sub.country_code));
    const allExcluded = children.every((sub: ICountry) => !marketsSet.has(sub.country_code));

    if (allIncluded) {
      areaCodes.push(area.area_code);
    }
    // 部分命中：不能用 region 模式表示，降级为 market 模式
    if (!allIncluded && !allExcluded) {
      scopeType = 'market';
    }
  });

  return { type: scopeType, regions: areaCodes, markets: normalizedMarkets };
};

/**
 * 获取所有国家编码列表
 * @param regions 地区列表
 * @return 国家编码列表
 */
export const getAllCountryCodesByRegions = (regions?: IRegion[] | null): string[] => {
  const countries = getAllCountriesByRegions(regions);
  return countries.map((country) => country?.country_code);
};

/**
 * 获取所有国家列表
 * @param regions 地区列表
 * @return 国家列表
 */
export const getAllCountriesByRegions = (regions?: IRegion[] | null): ICountry[] => {
  if (!Array.isArray(regions) || regions.length === 0) return [];
  return regions.flatMap((region) => region?.country_set || []);
};

/**
 * 获取国家名称
 * @param locale 当前语言环境
 * @param countryCode 国家code
 * @param countryMap 国家映射
 * @return 国家名称
 */
export const getCountryNameFromCountryMap = (
  locale: string,
  countryCode: string,
  countryMap: IRecord<IRegionCountryEntity>,
): string => {
  if (isEmpty(countryCode)) {
    return '';
  }

  if (isEmpty(countryMap)) {
    return countryCode as string;
  }

  const country = countryMap[countryCode.toLowerCase()];
  const countryNameEN = country?.country_name_en || '';
  const countryNameCN = country?.country_name_cn || '';

  return getOptionText(locale, {
    value: countryCode,
    text: countryNameEN,
    textEN: countryNameEN,
    textCN: countryNameCN,
  });
};

/**
 * 翻译国家名称（根据国家列表）
 * @param locale 当前语言环境
 * @param params 参数对象
 * @param params.countries 国家列表
 * @param params.countryNameEN 国家英文名
 */
export const formatMessageCountryNameByNameEN = (
  locale: string,
  params: {
    countries: ICountry[] | null;
    countryNameEN: string;
  },
) => {
  const countries = params?.countries || [];
  const countryNameEN = params?.countryNameEN || '';

  if (!countryNameEN) return '';
  if (!Array.isArray(countries) || countries.length === 0) return countryNameEN;

  const country = countries.find((item) => {
    return item?.country_name_en?.toLowerCase() === countryNameEN.toLowerCase();
  });
  const marketNameEN = country?.country_name_en || '';
  const marketNameCN = country?.country_name_ch || '';

  return getOptionText(locale, {
    value: countryNameEN,
    text: marketNameEN,
    textEN: marketNameEN,
    textCN: marketNameCN,
  });
};

/**
 * 获取仅勾选US的region筛选器的value（页面筛选器的value不是apiValue）
 * @param filterValue 页面筛选器的value
 * @return 页面筛选器的value（region筛选器调整为仅勾选US）
 */
export const getUSOnlyRegionFilterValue = (filterValue?: any) => {
  return {
    ...filterValue,
    [filterKeyMap.REGION]: {
      ...filterValue?.[filterKeyMap.REGION],
      type: 'market',
      regions: [],
      markets: ['us'],
      realMarkets: ['us'],
    },
  };
};

export const addGlobalRegion = (regions?: IRegion[] | null): IRegion[] => {
  if (!Array.isArray(regions)) {
    return [GLOBAL_REGION];
  }

  if (regions.find((region) => region?.area_code === GLOBAL_REGION.area_code)) {
    return regions;
  }

  return [GLOBAL_REGION, ...regions];
};

export const getRegionByCode = (regionCode?: string, regions?: IRegion[] | null): IRegion | null => {
  if (!regionCode) return null;
  if (regionCode === 'global') {
    return {
      area_code: regionCode,
      area_name_en: 'Global',
      area_name_ch: '全球',
      country_set: [],
    };
  }
  if (regionCode === 'global_wo_china') {
    return {
      area_code: regionCode,
      area_name_en: 'Global except China mainland',
      area_name_ch: '全球（不包括中国大陆）',
      country_set: [],
    };
  }
  if (!Array.isArray(regions) || regions.length === 0) return null;
  const region = regions.find((item) => regionCode === item?.area_code);
  return region || null;
};

export const getRegionByCountryCode = (countryCode?: string, regions?: IRegion[] | null): IRegion | null => {
  if (!countryCode) return null;
  if (countryCode === 'global') {
    return {
      area_code: countryCode,
      area_name_en: 'Global',
      area_name_ch: '全球',
      country_set: [],
    };
  }
  if (countryCode === 'global_wo_china') {
    return {
      area_code: countryCode,
      area_name_en: 'Global except China mainland',
      area_name_ch: '全球',
      country_set: [],
    };
  }
  if (!Array.isArray(regions) || regions.length === 0) return null;

  for (const region of regions) {
    const countries = region?.country_set || [];
    for (const country of countries) {
      if (countryCode === country?.country_code) {
        return region;
      }
    }
  }

  return null;
};

export const getCountryByCode = (countryCode?: string, regions?: IRegion[] | null): IRegionCountry | null => {
  if (!countryCode) return null;
  if (countryCode === 'global') {
    return {
      area_code: countryCode,
      area_name_en: 'Global',
      area_name_ch: '全球',
      country_code: countryCode,
      country_name_en: 'Global',
      country_name_ch: '全球',
    };
  }
  if (countryCode === 'global_wo_china') {
    return {
      area_code: countryCode,
      area_name_en: 'Global except China mainland',
      area_name_ch: '全球',
      country_code: countryCode,
      country_name_en: 'Global except China mainland',
      country_name_ch: '全球（不包括中国大陆）',
    };
  }
  if (!Array.isArray(regions) || regions.length === 0) return null;

  for (const region of regions) {
    const countries = region?.country_set || [];
    for (const country of countries) {
      if (countryCode === country?.country_code) {
        return {
          ...country,
          area_code: region?.area_code || '',
          area_name_en: region?.area_name_en || '',
          area_name_ch: region?.area_name_ch || '',
        };
      }
    }
  }

  return null;
};

export const addGlobalCountryCode = (countryCodes?: string[]): string[] => {
  if (!Array.isArray(countryCodes) || countryCodes.length === 0) {
    return ['global'];
  }

  if (countryCodes.includes('global')) {
    return countryCodes;
  }

  return ['global', ...countryCodes];
};

/**
 * 将 cond 接口返回值转换为 IRegion[] 格式
 * @param marketInfo cond 接口返回的 market_info 数据
 * @returns IRegion[] 地区国家数据
 */
export const formatRegionOptionsByCond = (marketInfo: any): IRegion[] => {
  if (!marketInfo) {
    return [];
  }

  const regions: IRegion[] = [];

  // 从 market_info 中获取映射关系
  const intelRegionToMarketMap = marketInfo['intel_region -> market'] || {};
  const intelRegionENMap = marketInfo['intel_region -> en'] || {};
  const intelRegionCNMap = marketInfo['intel_region -> cn'] || {};
  const marketENMap = marketInfo['market -> en'] || {};
  const marketCNMap = marketInfo['market -> cn'] || {};

  // 遍历地区编码，构建地区国家结构
  Object.keys(intelRegionToMarketMap).forEach((regionCode) => {
    const regionNameEN = intelRegionENMap[regionCode] || '';
    const regionNameCN = intelRegionCNMap[regionCode] || '';
    const marketCodes = intelRegionToMarketMap[regionCode] || [];

    // 获取该地区下的所有国家
    const countries: ICountry[] = marketCodes.map((marketCode: string) => {
      return {
        country_code: marketCode,
        country_name_en: marketENMap[marketCode] || '',
        country_name_ch: marketCNMap[marketCode] || '',
      };
    });

    regions.push({
      area_code: regionCode,
      area_name_en: regionNameEN,
      area_name_ch: regionNameCN,
      country_set: countries,
    });
  });

  return regions;
};
